import React, { useEffect, useState } from 'react';
import {
  getWhatsAppCredential,
  connectWhatsAppCredential,
  WhatsAppNumberConflictError,
  SafeWhatsAppCredential,
} from '../../api/backoffice';

/**
 * Per-account WhatsApp section (AC-W1…W10). Rendered inside an individual account's back-office page
 * (CustomerTelemetryPage), scoped to that accountId. Shows THAT account's WhatsApp connection and lets
 * the operator edit + save it.
 *
 * 🔴 The token is NEVER read back (AC-W4): the read endpoint returns `tokenConfigured` only. On a
 * connected account the token field reads as MASKED-SET ("on file") with helper text — leave blank to
 * keep the stored token, paste a new one only to replace it (AC-W6). The platform-global App Secret /
 * verify token are NOT here (AC-W8) — they stay on the central page.
 */
export const WhatsAppAccountSection: React.FC<{ accountId: string }> = ({ accountId }) => {
  const [cred, setCred] = useState<SafeWhatsAppCredential | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [phoneNumberId, setPhoneNumberId] = useState('');
  const [wabaId, setWabaId] = useState('');
  const [displayNumber, setDisplayNumber] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [save, setSave] = useState<{ kind: 'idle' | 'saving' | 'success' | 'error'; message?: string }>({ kind: 'idle' });

  // AC-W10 isolation. NOTE: the parent (CustomerTelemetryPage) REMOUNTS this section on every :id change —
  // its :144 loading gate unmounts the content during the refetch — so component-state carry-over cannot
  // occur HERE today (a property of the current parent, not a permanent guarantee). This refetch-and-reset
  // on [accountId] is a defensive redundancy: it is the only thing that would catch a FUTURE regression
  // adding module-scope/shared state to this section, which a remount would NOT reset. The isolation risk
  // the remount does NOT prevent is a wrong-account FETCH — that is what the in-session W10 test controls.
  useEffect(() => {
    let live = true;
    setLoading(true); setLoadError(false); setSave({ kind: 'idle' });
    setPhoneNumberId(''); setWabaId(''); setDisplayNumber(''); setAccessToken(''); setCred(null);
    getWhatsAppCredential(accountId)
      .then((c) => {
        if (!live) return;
        setCred(c);
        if (c) { setPhoneNumberId(c.phoneNumberId); setWabaId(c.wabaId); setDisplayNumber(c.displayNumber ?? ''); }
      })
      .catch(() => { if (live) setLoadError(true); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [accountId]);

  const handleSave = async () => {
    setSave({ kind: 'saving' });
    try {
      await connectWhatsAppCredential({
        accountId,
        phoneNumberId: phoneNumberId.trim(),
        wabaId: wabaId.trim(),
        displayNumber: displayNumber.trim() || undefined,
        // AC-W6: blank token → keep the stored one. Only send a token when the operator typed a new one.
        accessToken: accessToken.trim() || undefined,
      });
      setAccessToken(''); // never retain a typed token in component state longer than the request
      const fresh = await getWhatsAppCredential(accountId);
      setCred(fresh);
      if (fresh) { setPhoneNumberId(fresh.phoneNumberId); setWabaId(fresh.wabaId); setDisplayNumber(fresh.displayNumber ?? ''); }
      setSave({ kind: 'success', message: 'Saved.' });
    } catch (err: any) {
      // AC-W7: a cross-tenant number conflict is refused and explained, not silently reassigned.
      const message = err instanceof WhatsAppNumberConflictError
        ? `${err.message} (this number is already connected to a different account).`
        : (err?.message || 'Failed to save.');
      setSave({ kind: 'error', message });
    }
  };

  const connected = !!cred;
  const statusLabel = connected ? (cred!.status === 'connected' ? 'Connected' : cred!.status) : 'Not connected';
  const inputClass = 'w-full px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-white text-sm';

  return (
    <div data-testid="whatsapp-account-section" className="bg-gray-900 rounded-xl p-6 border border-gray-800">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">WhatsApp</h3>
        <span
          data-testid="wa-status"
          className={`px-2 py-1 rounded text-xs font-medium ${connected && cred!.status === 'connected' ? 'bg-green-700 text-green-100' : 'bg-gray-700 text-gray-200'}`}
        >
          {statusLabel}
        </span>
      </div>

      {loading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : loadError ? (
        <p className="text-sm text-red-400" data-testid="wa-load-error">Could not load the WhatsApp connection. Try again.</p>
      ) : (
        <div className="space-y-3">
          {!connected && (
            <p className="text-sm text-gray-400" data-testid="wa-not-connected">
              This account has no WhatsApp connection yet. Enter the WABA details and an access token to connect.
            </p>
          )}

          {/* ROUTING KEY — visually primary. This is the field that actually controls where inbound
              messages go (resolveTenantByPhoneNumberId), NOT the cosmetic display number below. */}
          <div className="rounded-lg border border-green-800/60 bg-green-950/20 p-3">
            <label className="block text-xs font-semibold text-green-300 mb-1">Phone number ID (routing key)</label>
            <input data-testid="wa-phone-number-id" className={`${inputClass} border-green-700`} value={phoneNumberId} onChange={(e) => setPhoneNumberId(e.target.value)} />
            <p className="text-xs text-gray-400 mt-1">The Meta <code>phone_number_id</code> — this is what routes inbound messages to this account. Must match the number configured in Meta.</p>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">WABA ID</label>
            <input data-testid="wa-waba-id" className={inputClass} value={wabaId} onChange={(e) => setWabaId(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Display number (label only)</label>
            <input data-testid="wa-display-number" className={inputClass} value={displayNumber} onChange={(e) => setDisplayNumber(e.target.value)} placeholder={phoneNumberId} />
            <p className="text-xs text-gray-500 mt-1">Shown in the console inbox; NOT used for routing.</p>
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Access token</label>
            {connected && cred!.tokenConfigured && (
              <p data-testid="wa-token-indicator" className="text-xs text-green-400 mb-1">Token: on file ••••</p>
            )}
            <input
              data-testid="wa-access-token"
              type="password"
              className={inputClass}
              value={accessToken}
              onChange={(e) => setAccessToken(e.target.value)}
              placeholder={connected && cred!.tokenConfigured ? 'Leave blank to keep the stored token' : 'Paste the WABA access token'}
            />
            {connected && cred!.tokenConfigured && (
              <p className="text-xs text-gray-500 mt-1">The token is stored and stays stored — leave blank to keep it; paste a new one only to replace it.</p>
            )}
          </div>

          <div className="flex items-center gap-3 pt-1">
            <button
              data-testid="wa-save"
              onClick={handleSave}
              disabled={save.kind === 'saving'}
              className="px-4 py-2 text-sm text-white bg-green-600 hover:bg-green-700 rounded-lg disabled:opacity-60"
            >
              {save.kind === 'saving' ? 'Saving…' : 'Save'}
            </button>
            {save.kind === 'success' && <span data-testid="wa-save-ok" className="text-sm text-green-400">{save.message}</span>}
            {save.kind === 'error' && <span data-testid="wa-save-error" className="text-sm text-red-400">{save.message}</span>}
          </div>
        </div>
      )}
    </div>
  );
};
